import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../webrtc/useSession';
import { useDesktopInput } from '../input/useDesktopInput';
import { useTouchTrackpad } from '../input/useTouchTrackpad';
import { SoftKeyboardOverlay, type SoftKeyboardHandle } from '../input/SoftKeyboardOverlay';
import { useClipboardSync } from '../clipboard/useClipboardSync';
import { sendInput } from '../input/protocol';
import { useMonitorPicker } from '../input/useMonitorPicker';
import { MonitorChip } from '../input/MonitorChip';
import { useFileUpload } from '../files/useFileUpload';
import { FileTransferOverlay } from '../files/FileTransferOverlay';
import { useAuthStore } from '../auth/store';
import { api, ApiError } from '../auth/api';
import { createStatsSampler, type StatsSnapshot } from '../webrtc/stats';

const isTouchDevice = (): boolean =>
  typeof window !== 'undefined' && (('ontouchstart' in window) || (navigator.maxTouchPoints ?? 0) > 0);

function buildSignalingUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

export function SessionPage() {
  const { hostId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateP = (location.state as { password?: string } | null)?.password;
  // Dev convenience: allow `?p=PASSWORD` in the URL so deep-links work without going through the
  // connect form. Production should drop this — passwords in URLs leak into history/logs.
  const urlP = new URLSearchParams(location.search).get('p') ?? undefined;
  const explicitPassword = stateP ?? urlP;
  const token = useAuthStore((s) => s.token);

  // Bounce to /login if there's neither password nor a logged-in user (trusted-device path needs auth).
  useEffect(() => {
    if (!explicitPassword && !token) navigate('/login', { replace: true, state: { from: location.pathname } });
  }, [explicitPassword, token, navigate, location.pathname]);

  if (!hostId) return null;
  if (!explicitPassword && !token) return null;

  return <SessionView hostId={hostId} password={explicitPassword ?? ''} bearerToken={token} />;
}

function SessionView({ hostId, password, bearerToken }: { hostId: string; password: string; bearerToken: string | null }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const signalingUrl = useMemo(buildSignalingUrl, []);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [surface, setSurface] = useState<HTMLDivElement | null>(null);
  // We deliberately do NOT keep `softKbVisible` or `textFieldHint` in React state any more —
  // each setState here triggered a top-level re-render and the Android HW video overlay flicked
  // black on every render. Instead we drive the keyboard imperatively (refs into the overlay
  // input) and toggle the button glow via direct DOM mutation through `kbBtnRef`.
  const kbHandleRef = useRef<SoftKeyboardHandle | null>(null);
  const kbBtnRef = useRef<HTMLElement | null>(null);
  const textHintTimerRef = useRef<number | null>(null);
  const [audioMuted, setAudioMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const touch = useMemo(isTouchDevice, []);
  // Quality selection reverted — the host encodes at a single fixed setting, no per-session choice.

  const toggleAudio = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setAudioMuted(el.muted);
    if (!el.muted) {
      // First user-gesture unmute kicks off audio playback — call play() in case autoplay was blocked.
      el.play().catch(() => {});
    }
  };

  // Toggle fullscreen on the session surface (video + overlays). When fullscreen the header is
  // hidden by the browser chrome; ESC also exits. We listen to `fullscreenchange` so the button's
  // label/icon stays in sync even if the user presses ESC or the browser-native fullscreen UI.
  const toggleFullscreen = () => {
    const target = surfaceRef.current;
    if (!target) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      target.requestFullscreen().catch(() => {});
    }
  };
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // "Save this machine" — claim host into the user's trusted devices. State declared up here so
  // it survives renders; the canClaim derived flag is computed below (after `session` exists).
  const [claimState, setClaimState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [claimError, setClaimError] = useState<string | null>(null);
  const claimMachine = async () => {
    if (!bearerToken || !password) return;
    setClaimState('saving');
    setClaimError(null);
    try {
      await api.claimDevice(bearerToken, hostId, password);
      setClaimState('saved');
    } catch (err) {
      setClaimState('error');
      if (err instanceof ApiError) setClaimError(err.code);
      else setClaimError('save_failed');
    }
  };

  const session = useSession({ signalingUrl, hostId, password, bearerToken });
  // Claim is offered only when the user is logged in AND they used the ephemeral password to
  // connect (i.e. they've proven physical access to the host). Once already saved, suppress.
  const canClaim = !!bearerToken && !!password && session.phase.kind === 'connected' && claimState === 'idle';

  useEffect(() => {
    session.attachVideo(videoRef.current);
    setVideoEl(videoRef.current);
    setSurface(surfaceRef.current);
    return () => {
      session.attachVideo(null);
      setVideoEl(null);
      setSurface(null);
    };
  }, [session]);

  // Desktop mouse + keyboard (no-op on pure touch devices since events don't fire on the video).
  useDesktopInput({
    videoEl,
    inputChannel: session.inputChannel,
    enabled: session.phase.kind === 'connected' && !touch,
  });

  // Touch trackpad for mobile. The cursor element is mutated via `cursorElRef` (direct DOM
  // style writes) so finger drags don't trigger React re-renders — important on Android
  // Chromium where every re-render of the parent flickers the `<video>` HW overlay.
  const cursorElRef = useRef<HTMLDivElement | null>(null);
  const { syncCursor, sendMouseButton } = useTouchTrackpad({
    surface,
    videoElRef: videoRef,
    zoomTargetRef: videoRef,
    inputChannel: session.inputChannel,
    enabled: session.phase.kind === 'connected' && touch,
    cursorElRef,
  });

  // Listen to the host's CursorPosition broadcasts (control DC, ~30 Hz) and pin the local
  // virtual-cursor overlay to where the OS mouse really is. Keeps the green crosshair honest
  // even when the host moves the mouse directly without phone touches, and aligns the starting
  // point of the next finger-drag with reality.
  useEffect(() => {
    const ch = session.controlChannel;
    if (!ch || !touch) return;
    const onMsg = (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      try {
        const parsed = JSON.parse(ev.data);
        if (parsed?.type === 'cursor_position' && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          syncCursor(parsed.x, parsed.y);
        } else if (parsed?.type === 'text_edit_focus' && typeof parsed.active === 'boolean') {
          // Edge-trigger the button hint via direct DOM mutation — no React state, no
          // re-render, no compositor flicker. We also debounce: the host sends a transition
          // every time the IBEAM cursor appears (mouseover) or disappears (mouseout), and a
          // user dragging across a window edge can churn that toggle at 5-10 Hz. Without the
          // debounce, every flip would mutate the button class which on Yandex Android
          // invalidates the surrounding compositor layer and the video flicks.
          const desired = parsed.active;
          if (textHintTimerRef.current !== null) {
            window.clearTimeout(textHintTimerRef.current);
          }
          textHintTimerRef.current = window.setTimeout(() => {
            textHintTimerRef.current = null;
            const btn = kbBtnRef.current;
            if (!btn) return;
            // Static glow only — no `animate-pulse`. Animations on the overlay button repaint
            // their containing block 60 Hz and likewise jitter the HW video underneath.
            btn.style.boxShadow = desired ? '0 0 18px 3px rgba(74,222,128,0.85)' : '';
          }, 250);
        }
      } catch {
        /* clipboard messages are also routed via this channel; ignore non-JSON / unknown types */
      }
    };
    ch.addEventListener('message', onMsg);
    return () => ch.removeEventListener('message', onMsg);
  }, [session.controlChannel, syncCursor, touch]);

  // Bidirectional clipboard text sync over the `control` DataChannel.
  useClipboardSync({
    controlChannel: session.controlChannel,
    enabled: session.phase.kind === 'connected',
  });

  // Multi-monitor picker — populated by `MonitorList` messages from the host. The chip only
  // renders if there's more than one monitor, so single-display setups stay clutter-free.
  const monitorPicker = useMonitorPicker(session.controlChannel);

  // File upload (browser → host) over the `files` DataChannel.
  const { transfers, upload, cancel, clear } = useFileUpload({
    filesChannel: session.filesChannel,
    enabled: session.phase.kind === 'connected',
  });
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  // Drag-and-drop wiring on the session surface. We track dragenter / dragleave with a counter to
  // avoid flicker as the cursor crosses child element boundaries.
  useEffect(() => {
    if (!surface) return;
    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setDragActive(true);
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setDragActive(false);
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setDragActive(false);
      if (session.phase.kind !== 'connected') return;
      const list = e.dataTransfer?.files;
      if (!list) return;
      for (const f of Array.from(list)) upload(f);
    };
    surface.addEventListener('dragenter', onDragEnter);
    surface.addEventListener('dragleave', onDragLeave);
    surface.addEventListener('dragover', onDragOver);
    surface.addEventListener('drop', onDrop);
    return () => {
      surface.removeEventListener('dragenter', onDragEnter);
      surface.removeEventListener('dragleave', onDragLeave);
      surface.removeEventListener('dragover', onDragOver);
      surface.removeEventListener('drop', onDrop);
    };
  }, [surface, session.phase.kind, upload]);

  return (
    <main className="session-shell">
      <header className="session-header flex items-center justify-between bg-gradient-to-b from-[var(--color-surface)] to-[var(--color-bg)]/30 backdrop-blur-md border-b border-[var(--color-border)]">
        <div className="flex items-center gap-2.5 min-w-0">
          <Link
            to="/"
            className="flex items-center justify-center w-8 h-8 -ml-1.5 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5 text-lg transition"
            aria-label="Back to connect"
          >
            ←
          </Link>
          <span className="font-mono text-sm sm:text-base font-semibold tracking-[0.18em] text-[var(--color-text)] truncate">
            {hostId.slice(0, 3)} {hostId.slice(3, 6)} {hostId.slice(6, 9)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {canClaim && (
            <button
              type="button"
              onClick={claimMachine}
              className="text-xs px-2 py-1 rounded border border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-black transition"
              title="Add this machine to your account for one-click access"
            >
              💾 save machine
            </button>
          )}
          {claimState === 'saving' && (
            <span className="text-xs text-[var(--color-text-muted)]">saving…</span>
          )}
          {claimState === 'saved' && (
            <span className="text-xs text-[var(--color-accent)]">✓ saved</span>
          )}
          {claimState === 'error' && (
            <span className="text-xs text-[var(--color-danger)]" title={claimError ?? undefined}>× save failed</span>
          )}
          {session.phase.kind === 'connected' && (
            <button
              type="button"
              onClick={toggleAudio}
              className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-border)] transition"
              aria-label={audioMuted ? 'Unmute remote audio' : 'Mute remote audio'}
              title={audioMuted ? 'Browser autoplay policy keeps audio muted until you click here' : 'Click to mute'}
            >
              {audioMuted ? '🔇 unmute' : '🔊 mute'}
            </button>
          )}
          {session.phase.kind === 'connected' && (
            <button
              type="button"
              onClick={toggleFullscreen}
              className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-border)] transition"
              aria-label={isFullscreen ? 'Выйти из полноэкранного режима' : 'Развернуть на весь экран'}
              title={isFullscreen ? 'Свернуть (или нажми Esc)' : 'На весь экран'}
            >
              {isFullscreen ? '🗗 свернуть' : '⛶ во весь экран'}
            </button>
          )}
          <PhaseBadge phase={session.phase} peer={session.peerStatus.kind} />
        </div>
      </header>

      <div
        ref={surfaceRef}
        // Any touch on the desktop area blurs the hidden keyboard input. Android Chromium
        // keeps focus on the input even after the user dismisses the on-screen keyboard via
        // the system back button, and a focused text input + any user gesture causes the OS to
        // auto-reopen the keyboard. Blurring on touch makes the dismiss "stick" — subsequent
        // taps don't pop the keyboard up. A deliberate tap on the ⌨ label re-focuses through
        // the htmlFor pathway and reopens it on demand. `.blur()` is idempotent when the input
        // isn't focused, so non-keyboard interactions pay zero cost.
        onTouchStart={() => kbHandleRef.current?.blur()}
        className="flex-1 relative bg-black overflow-hidden"
        style={{ touchAction: touch ? 'none' : undefined }}
      >
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-contain bg-black ${touch ? 'cursor-none' : 'cursor-default'}`}
          playsInline
          autoPlay
          muted
          tabIndex={0}
        />
        {touch && session.phase.kind === 'connected' && (
          <VirtualCursor ref={cursorElRef} />
        )}
        {/* The ⌨ label used to live here, inside `surface`, but `surface` carries
            `touch-action: none` AND a `preventDefault()`-ing touchstart listener from
            `useTouchTrackpad`. Both swallow the synthesised click that a label tap would
            otherwise dispatch, so the input never received focus. Moved out below to a `fixed`
            position relative to <main>, outside the trackpad's touch-action zone. */}
        <FileTransferOverlay
          transfers={transfers}
          dragActive={dragActive}
          onCancel={cancel}
          onClear={clear}
        />
        {/* Monitor picker — only visible when the host has >1 monitor. The chip lives at the
            top-right of the session surface so it doesn't fight the keyboard button on phones. */}
        <MonitorChip monitors={monitorPicker.monitors} onSelect={monitorPicker.select} />
        {session.phase.kind !== 'connected' && <Overlay session={session} />}
      </div>

      <SoftKeyboardOverlay
        ref={kbHandleRef}
        inputChannel={session.inputChannel}
        onHide={() => {
          /* nothing — we no longer track a `visible` flag; the browser owns keyboard state. */
        }}
      />

      <StatsBar pcRef={session.pcRef} phase={session.phase} peerStatus={session.peerStatus} />

      {/* Bottom touch control bar + combos sheet. Docked (in flex flow), so it never overlaps the
          video and is always above the browser's bottom chrome (safe-area handled in CSS). Lives
          in its own component so opening/closing the combos sheet re-renders ONLY the controls,
          never the <video> (which would flick the HW overlay on Android). */}
      {touch && session.phase.kind === 'connected' && (
        <TouchControls
          inputChannel={session.inputChannel}
          sendMouseButton={sendMouseButton}
          kbBtnRef={kbBtnRef}
        />
      )}
    </main>
  );
}

/** Common remote key combos. `mods` are held (as real key-down/up events — the host derives
 *  modifier state from physical key injections, it ignores the modifiers bitmap) around `key`. */
const KEY_COMBOS: ReadonlyArray<{ label: string; mods: string[]; key: string }> = [
  { label: 'Ctrl+C', mods: ['ControlLeft'], key: 'KeyC' },
  { label: 'Ctrl+V', mods: ['ControlLeft'], key: 'KeyV' },
  { label: 'Ctrl+X', mods: ['ControlLeft'], key: 'KeyX' },
  { label: 'Ctrl+Z', mods: ['ControlLeft'], key: 'KeyZ' },
  { label: 'Ctrl+A', mods: ['ControlLeft'], key: 'KeyA' },
  { label: 'Ctrl+S', mods: ['ControlLeft'], key: 'KeyS' },
  { label: 'Alt+Tab', mods: ['AltLeft'], key: 'Tab' },
  { label: 'Alt+F4', mods: ['AltLeft'], key: 'F4' },
  { label: '⊞ Win', mods: [], key: 'MetaLeft' },
  { label: 'Esc', mods: [], key: 'Escape' },
  { label: 'Tab', mods: [], key: 'Tab' },
  { label: 'Enter', mods: [], key: 'Enter' },
  { label: 'Del', mods: [], key: 'Delete' },
  { label: '⌫', mods: [], key: 'Backspace' },
  { label: 'Ctrl+Alt+Del', mods: ['ControlLeft', 'AltLeft'], key: 'Delete' },
];

const ARROW_KEYS: ReadonlyArray<{ label: string; key: string }> = [
  { label: '←', key: 'ArrowLeft' },
  { label: '↑', key: 'ArrowUp' },
  { label: '↓', key: 'ArrowDown' },
  { label: '→', key: 'ArrowRight' },
];

/**
 * Bottom control bar: keyboard, left/right mouse, and a combos sheet — all docked, thumb-sized,
 * and safe-area-aware. Self-contained state (`combosOpen`) so toggling the sheet doesn't re-render
 * the parent session view (and therefore never flicks the HW <video> overlay on Android).
 */
function TouchControls({
  inputChannel,
  sendMouseButton,
  kbBtnRef,
}: {
  inputChannel: RTCDataChannel | null;
  sendMouseButton: (button: 'left' | 'right' | 'middle', pressed: boolean) => void;
  kbBtnRef: React.RefObject<HTMLElement | null>;
}) {
  const [combosOpen, setCombosOpen] = useState(false);

  const sendKey = (mods: string[], key: string) => {
    const ch = inputChannel;
    if (!ch) return;
    for (const m of mods) sendInput(ch, { type: 'key_down', code: m, modifiers: {} });
    sendInput(ch, { type: 'key_down', code: key, modifiers: {} });
    sendInput(ch, { type: 'key_up', code: key, modifiers: {} });
    for (const m of [...mods].reverse()) sendInput(ch, { type: 'key_up', code: m, modifiers: {} });
    buzz(8);
  };

  return (
    <div className="relative shrink-0">
      {combosOpen && (
        <div className="combos-sheet">
          <div className="flex items-center justify-between mb-2 px-0.5">
            <span className="text-[11px] uppercase tracking-[0.15em] text-[var(--color-text-muted)]">
              Спец-клавиши
            </span>
            <button
              type="button"
              onClick={() => setCombosOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5"
              aria-label="Закрыть"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {KEY_COMBOS.map((c) => (
              <button
                key={c.label}
                type="button"
                className="combo-key"
                style={{ touchAction: 'manipulation' }}
                onClick={() => sendKey(c.mods, c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {ARROW_KEYS.map((a) => (
              <button
                key={a.key}
                type="button"
                className="combo-key text-lg"
                style={{ touchAction: 'manipulation' }}
                onClick={() => sendKey([], a.key)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="control-bar">
        <label
          ref={kbBtnRef as React.RefObject<HTMLLabelElement>}
          htmlFor="linkora-kbd-input"
          className="cb-btn w-12 h-12 text-xl text-[var(--color-accent)] cursor-pointer"
          style={{ touchAction: 'manipulation' }}
          aria-label="Клавиатура"
        >
          ⌨
        </label>
        <button
          type="button"
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => { e.preventDefault(); buzz(12); sendMouseButton('left', true); }}
          onTouchEnd={(e) => { e.preventDefault(); sendMouseButton('left', false); }}
          onTouchCancel={() => sendMouseButton('left', false)}
          className="cb-btn cb-primary w-[88px] h-12"
          style={{ touchAction: 'manipulation' }}
          aria-label="Левая кнопка мыши (тап = клик, удерживать = тащить)"
        >
          <span className="text-base font-extrabold leading-none">ЛКМ</span>
        </button>
        <button
          type="button"
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => { e.preventDefault(); buzz(8); sendMouseButton('right', true); }}
          onTouchEnd={(e) => { e.preventDefault(); sendMouseButton('right', false); }}
          onTouchCancel={() => sendMouseButton('right', false)}
          className="cb-btn w-16 h-12"
          style={{ touchAction: 'manipulation' }}
          aria-label="Правая кнопка мыши"
        >
          <span className="text-sm font-bold leading-none">ПКМ</span>
        </button>
        <button
          type="button"
          onClick={() => setCombosOpen((o) => !o)}
          className={`cb-btn w-14 h-12 ${combosOpen ? 'text-[var(--color-accent)]' : 'text-white'}`}
          style={{ touchAction: 'manipulation' }}
          aria-label="Спец-клавиши"
        >
          <span className="text-lg leading-none">⌥</span>
          <span className="text-[8px] tracking-wide opacity-70 leading-none mt-0.5">КЛАВ</span>
        </button>
      </div>
    </div>
  );
}

/** Best-effort haptic tick on press. No-op where the Vibration API is unavailable (iOS Safari). */
function buzz(ms: number): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* unsupported — silent */
  }
}

/** Visible crosshair showing the virtual-cursor position. Position is driven by direct
 * `style.left/.top` writes from `useTouchTrackpad` (via the forwarded ref) so it doesn't
 * participate in React's render cycle — the parent stays stable, the `<video>` doesn't repaint.
 *
 * Visual recipe (tuned for small phone screens):
 *   - 44 px outer ring (≈ Apple HIG minimum touch target) with strong green stroke + soft glow.
 *   - 6 px solid centre dot in pure accent green for unambiguous "this is where the mouse is".
 *   - Slow 1.4 s breathing animation on the outer ring → eye picks it up against any background
 *     even when the remote desktop is busy.
 */
const VirtualCursor = forwardRef<HTMLDivElement>((_props, ref) => (
  <div
    ref={ref}
    className="absolute pointer-events-none z-10"
    style={{
      left: '50%',
      top: '50%',
      width: 24,
      height: 24,
      transform: 'translate(-50%, -50%)',
      willChange: 'left, top',
    }}
  >
    <svg viewBox="0 0 24 24" width="24" height="24">
      <circle cx="12" cy="12" r="10" fill="rgba(74,222,128,0.18)" stroke="rgba(74,222,128,0.9)" strokeWidth="2" />
      <circle cx="12" cy="12" r="2" fill="rgba(74,222,128,1)" />
    </svg>
  </div>
));

function PhaseBadge({ phase, peer }: { phase: ReturnType<typeof useSession>['phase']; peer: string }) {
  const label = phaseLabel(phase, peer);
  const tone = phase.kind === 'connected' ? 'text-[var(--color-accent)]' : phase.kind === 'failed' ? 'text-[var(--color-danger)]' : 'text-[var(--color-text-muted)]';
  return <span className={`text-xs uppercase tracking-wider ${tone}`}>{label}</span>;
}

function phaseLabel(phase: ReturnType<typeof useSession>['phase'], peer: string): string {
  if (phase.kind === 'connected') return `live · ${peer}`;
  if (phase.kind === 'failed') return `failed · ${phase.reason}`;
  if (phase.kind === 'idle') return 'idle';
  if (phase.kind === 'connecting_signaling') return 'connecting…';
  if (phase.kind === 'authenticating') return 'authenticating…';
  if (phase.kind === 'negotiating') return 'negotiating…';
  return 'unknown';
}

function Overlay({ session }: { session: ReturnType<typeof useSession> }) {
  const message = phaseMessage(session.phase);
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="bg-black/70 border border-[var(--color-border)] rounded-md px-6 py-4 text-center pointer-events-auto max-w-md">
        <p className="text-sm mb-3">{message}</p>
        {session.phase.kind === 'failed' && (
          <Link to="/" className="text-xs text-[var(--color-accent)] hover:underline">
            ← back to connect
          </Link>
        )}
      </div>
    </div>
  );
}

function phaseMessage(phase: ReturnType<typeof useSession>['phase']): string {
  switch (phase.kind) {
    case 'idle':
      return 'Initializing…';
    case 'connecting_signaling':
      return 'Connecting to signaling…';
    case 'authenticating':
      return 'Authenticating with host…';
    case 'negotiating':
      return 'Negotiating WebRTC…';
    case 'connected':
      return 'Live';
    case 'failed':
      return phase.reason;
  }
}

/**
 * Stats bar — owns its own polling + state so high-frequency `setState` updates do NOT
 * propagate to the parent `SessionPage` and trigger compositor flips on the `<video>` element
 * (which would manifest as a black flash on Android Chromium / Yandex Browser).
 *
 * Poll interval bumped to 2s — visual refresh of fps/rtt at 1Hz is plenty for a status bar; the
 * previous 1Hz interval combined with paint-amplification on mobile compositors caused the
 * "1 раз в секунду мигает" symptom.
 */
function StatsBar({
  pcRef,
  phase,
  peerStatus,
}: {
  pcRef: React.RefObject<RTCPeerConnection | null>;
  phase: ReturnType<typeof useSession>['phase'];
  peerStatus: ReturnType<typeof useSession>['peerStatus'];
}) {
  const [s, setS] = useState<StatsSnapshot | null>(null);

  useEffect(() => {
    if (phase.kind !== 'connected') return;
    const pc = pcRef.current;
    if (!pc) return;
    const sampler = createStatsSampler(pc);
    let mounted = true;
    const timer = window.setInterval(async () => {
      try {
        const snap = await sampler.sample();
        if (mounted) setS(snap);
      } catch {
        /* ignore single-tick failures */
      }
    }, 2000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [phase.kind, pcRef]);

  return (
    <footer className="shrink-0 text-[10px] sm:text-xs font-mono text-[var(--color-text-muted)] px-3 py-1 bg-[var(--color-surface)] border-t border-[var(--color-border)] flex flex-nowrap gap-x-3 overflow-x-auto whitespace-nowrap">
      <span>peer: {peerStatus.kind}</span>
      {s?.video && (
        <>
          <span>
            res: {s.video.width ?? '?'}×{s.video.height ?? '?'}
          </span>
          <span>fps: {s.video.framesPerSecond?.toFixed(0) ?? '–'}</span>
          <span>br: {s.video.bitrateKbps ?? '–'} kbps</span>
          {s.video.codec && <span>codec: {s.video.codec.replace('video/', '')}</span>}
          {s.video.packetsLost !== undefined && <span>loss: {s.video.packetsLost}</span>}
        </>
      )}
      {s?.transport && (
        <>
          {s.transport.currentRttMs !== undefined && <span>rtt: {s.transport.currentRttMs.toFixed(0)} ms</span>}
          {s.transport.localCandidateType && (
            <span>
              ice: {s.transport.localCandidateType}↔{s.transport.remoteCandidateType}
            </span>
          )}
        </>
      )}
    </footer>
  );
}
